<template>
  <div class="Content clearfix">
    <div class="Content-group">
      <div class="Section Section--flex">
        <h2 class="Section-header no-margin">
          E-mail Automation Lists
        </h2>
        <a
          v-if="canCreate"
          href="/search"
          class="Button Button--green js-loadingState"
        >
          Create E-mail Automation List
        </a>
      </div>
      <div
        v-if="ui.hideFilters"
        class="Section"
      >
        <div class="u-floatRight">
          <div class="SearchInputGroup">
            <rmp-input
              v-model="additionalParams.search"
              :clearable="true"
              input-type="text"
              style="width: 380px"
              placeholder="Search by e-mail automation list name or workflow name..."
              @cleared="submitSearch"
              @keypress.enter="submitSearch"
            />
            <button
              class="Button Button--blue"
              @click="submitSearch"
            >
              <i class="Icon Icon-search" />
            </button>
          </div>
        </div>
      </div>
      <div class="Section">
        <div
          v-if="loadingState"
          class="js-loading"
        />
        <place-holder
          v-if="ui.showPlaceHolder"
          :search="additionalParams.search"
          lang-key="contact"
        />
        <div
          :class="['DataTable',{'DataTable--hide': ui.showPlaceHolder}]"
        >
          <vuetable-pagination-info
            ref="paginationInfo"
            :css="paginationInfoConfig.css"
            :info-template="table.paginationInfoConfig.infoTemplate"
          />
          <vue-table
            ref="vuetable"
            :append-params="additionalParams"
            :api-url="table.options.apiUrl"
            :http-fetch="getTableData"
            :fields="table.options.columns"
            :css="tableConfig.css"
            pagination-path=""
            @vuetable:loading="loadingState = true"
            @vuetable:loaded="setTableStatus"
            @vuetable:load-success="setCanCreate"
            @vuetable:pagination-data="onPaginationData"
            @vuetable:load-error="handleDataTableError"
          >
            <div
              slot="name"
              slot-scope="props"
            >
              {{ props.rowData.name }}
            </div>
            <span
              slot="actions"
              slot-scope="props"
              class="Table"
            >
              <a
                v-if="props.rowData.no_of_students > 0"
                :href="`/email-automation-lists/${props.rowData.id}/students`"
                class="Table-action"
              >
                <tool-tip>
                  <i
                    slot="trigger"
                    class="Icon Icon-eye"
                  />
                  <p slot="tip">View Students</p>
                </tool-tip>
              </a>
              <span
                v-else
                class="Table-action Table--actionDisabled"
              >
                <tool-tip>
                  <i
                    slot="trigger"
                    class="Icon Icon-eye"
                  />
                  <p slot="tip">No Students</p>
                </tool-tip>
              </span>
              <a
                v-if="props.rowData.view_filters_url !== '[]'"
                :href="`/email-automation-lists/${props.rowData.id}/filters`"
                class="Table-action"
              >
                <tool-tip>
                  <i
                    slot="trigger"
                    class="Icon Icon-filter"
                  />
                  <p slot="tip">View E-mail Automation List Criteria</p>
                </tool-tip>
              </a>
              <span
                v-else
                class="Table-action Table--actionDisabled"
              >
                <tool-tip>
                  <i
                    slot="trigger"
                    class="Icon Icon-filter"
                  />
                  <p slot="tip">No E-mail Automation List Criteria</p>
                </tool-tip>
              </span>
              <rmp-modal
                v-if="props.rowData.can_delete"
                :ref="`deleteModal_${props.rowData.id}`"
                :show-cancel-button="true"
                extra-classes="Error Center Compact"
              >
                <tool-tip
                  slot="trigger"
                  @click.native="launchDeleteModal(props.rowData.id)"
                >
                  <span
                    slot="trigger"
                    class="Table-action"
                  >
                    <i
                      class="Icon Icon-trash"
                    />
                  </span>
                  <p
                    slot="tip"
                    class="u-text-center"
                  >Delete and cancel student workflows</p>
                </tool-tip>
                <h2
                  slot="header"
                  class="u-text-center"
                >
                  Delete - {{ props.rowData.name }}
                </h2>
                <div slot="body">
                  <p>Are you sure you want to delete <b>{{ props.rowData.name }}</b>?</p>
                  <small>By deleting an e-mail automation list, you are also cancelling all ongoing student workflow journeys associated with the list.</small>
                </div>
                <button
                  slot="footer"
                  :class="['Button Button--danger',{'is-loading': ui.loading }]"
                  @click.prevent="removeAnEmailAutomationList(props.rowData.id)"
                >
                  Delete
                </button>
              </rmp-modal>
              <span
                v-else
                class="Table-action Table--actionDisabled"
              >
                <tool-tip>
                  <i
                    slot="trigger"
                    class="Icon Icon-trash"
                  />
                  <p slot="tip">You don't have permission to delete</p>
                </tool-tip>
              </span>
            </span>
          </vue-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import DataTableMixin from '../../../mixins/datatable-mixin';
import PlaceHolder from '../common/no-data-placeholder.vue';
import RmpModal from '../../common/modals.vue';
import RmpTooltip from '../../common/tooltip.vue';

export default {
  name: 'EmailAutomationList',
  components: {
    'place-holder': PlaceHolder,
    'tool-tip': RmpTooltip,
    'rmp-modal': RmpModal,
  },
  mixins: [DataTableMixin],
  data() {
    return {
      canCreate: false,
      loadingState: false,
      ui: {
        hideFilters: false,
        loading: false,
        showPlaceHolder: false,
      },
      table: {
        paginationInfoConfig: {
          infoTemplate: '{from} - {to} of {total} E-mail Automation Lists',
        },
        options: {
          apiUrl: '/ajax/email-automation-lists',
          columns: [
            {
              name: 'name',
              sortField: 'name',
              title: 'List Name',
            },
            {
              name: 'workflow_name',
              sortField: 'workflow_name',
              title: 'Workflow Name',
            },
            {
              name: 'no_of_students',
              sortField: 'no_of_students',
              title: 'Students',
            },
            {
              name: 'created_at',
              sortField: 'created_at',
              title: 'Created',
            },
            { name: 'actions' },
          ],
        },
      },
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapActions(['deleteAnEmailAutomationList']),
    launchDeleteModal(id) {
      this.$refs[`deleteModal_${id}`].ui.showElement = true;
    },
    removeAnEmailAutomationList(id) {
      this.ui.loading = true;
      this.deleteAnEmailAutomationList(id)
        .then(({ data }) => {
          // success
          this.$bus.$emit('global-dismiss-modal'); // Dismiss modal
          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'info',
          });
          this.ui.loading = false;
          this.$refs.vuetable.refresh();
        })
        .catch(({ data }) => {
          this.ui.loading = false;
          this.$bus.$emit('global-dismiss-modal'); // Dismiss modal
          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'warning',
          });
        });
    },
    init() {
    },
    setCanCreate(payload) {
      this.canCreate = payload.data.can_create;
    },
  },
};
</script>
